import { useEffect, useRef, useState } from "react";
import { isAxiosError } from "axios";
import type { CreativeItem } from "@digital-twins/shared-types";
import { creativeLibraryApi } from "@/lib/api/creative-library";

/**
 * Attached references missing from the editor's list are checked one by one:
 * the list can still be loading or be capped, so only a 404 means unavailable.
 * Until the answer arrives, or if the check fails, the token gets no row.
 * The server already named the tokens this viewer cannot read: those stay
 * attached and are never checked, so a 404 here means the reference is gone.
 */
export function useAttachedReferences(
  open: boolean,
  tokens: Array<string>,
  listed: Array<CreativeItem>,
  hiddenTokens: Array<string> = [],
) {
  const [results, setResults] = useState<Record<string, CreativeItem | null>>(
    {},
  );
  const requested = useRef(new Set<string>());
  const session = useRef(0);
  const listedTokens = new Set(listed.map((item) => item.token));
  const unlisted = tokens.filter((token) => !listedTokens.has(token));
  const isHidden = (token: string) => hiddenTokens.includes(token);
  const unlistedKey = unlisted.filter((token) => !isHidden(token)).join(",");
  useEffect(() => {
    if (!open) {
      session.current += 1;
      requested.current = new Set();
      setResults({});
      return;
    }
    const opened = session.current;
    for (const token of unlistedKey ? unlistedKey.split(",") : []) {
      if (requested.current.has(token)) {
        continue;
      }
      requested.current.add(token);
      creativeLibraryApi
        .creative(token)
        .then((item) => {
          if (session.current === opened) {
            setResults((previous) => ({ ...previous, [token]: item }));
          }
        })
        .catch((error: unknown) => {
          if (
            session.current === opened &&
            isAxiosError(error) &&
            error.response?.status === 404
          ) {
            setResults((previous) => ({ ...previous, [token]: null }));
          }
        });
    }
  }, [open, unlistedKey]);
  return {
    found: unlisted.flatMap((token) => results[token] ?? []),
    unavailable: unlisted.filter(
      (token) => !isHidden(token) && results[token] === null,
    ),
    hidden: unlisted.filter(isHidden),
  };
}
